import React, { useEffect, useRef } from 'react';
import { Lock, Users } from 'lucide-react';
import { newId } from './boardHelpers';
import { Modal } from '../ui/Modal';
import CompactSelectPicker from '../ui/CompactSelectPicker';
import OsScroll from '../ui/OsScroll';
import BoardLabelEditor from '../BoardLabelEditor';
import BoardEditOptions from './BoardEditOptions';
import BoardEditFooter from './BoardEditFooter';
import BoardColorField from './BoardColorField';
import BoardKeyPrefixField, { keyPrefixError } from './BoardKeyPrefixField';
import { boardEditSubtitle, BOARD_DESCRIPTION_MAX } from '../../extensions/bundled/task-manager/boardEditStats';
import './BoardEditModal.css';

/** The create / edit board modal. Opened from a board tile and from the
 *  settings gear of an open board; TaskBoardTab owns its state and submit
 *  (useBoardAdmin).
 *
 *  Boards Redesign list 5 (12.10.2026): header subtitle with the board's
 *  numbers (`boardModal.stats`, edit only), the label palette as a bordered
 *  list with usage counts and a split new-label input, the options as switch
 *  rows, and a footer with "Delete board" (edit only, through the usual
 *  confirm — `onDeleteBoard`). */
export default function BoardEditModal({ boardModal, setBoardModal, shareableTeams, submitBoardModal, onDeleteBoard, takenPrefixes = [] }) {
  const boardDescRef = useRef(null);
  // The board description grows to its content so the TEXTAREA never scrolls
  // itself: a textarea's own scrollbar is the native one, and OverlayScrollbars
  // cannot restyle it. The overflow has to happen on the OsScroll wrapper,
  // where the app's bar lives (same shape as DbCellDetailModal's editor).
  useEffect(() => {
    const node = boardDescRef.current;
    if (!node) return;
    node.style.height = 'auto';
    node.style.height = `${node.scrollHeight}px`;
  }, [boardModal?.description, boardModal?.mode, boardModal?.id]);
  const descLength = (boardModal.description || '').length;

  return (
    <Modal
      isOpen
      onClose={() => setBoardModal(null)}
      title={boardModal.mode === 'create' ? 'Create board' : 'Edit board'}
      subtitle={boardModal.mode === 'edit' ? boardEditSubtitle(boardModal.stats) : ''}
      size="lg"
      contentClassName="board-edit-modal"
    >
      {/* Fields scroll (app scroll bar) on a short window; the footer stays. */}
      <OsScroll className="board-edit-scroll" options={{ overflow: { x: 'hidden' } }}>
        <div className="board-edit-body">
          {boardModal.mode === 'create' && (
            <div>
              <label className="board-edit-label" style={{ display: 'block', marginBottom: 6 }}>
                Visibility
              </label>
              <div style={{ display: 'flex', gap: 8 }}>
                {[
                  { key: 'private', label: 'Private', Icon: Lock },
                  { key: 'team', label: 'Team', Icon: Users },
                ].map(({ key, label, Icon }) => {
                  const active = (boardModal.visibility || 'private') === key;
                  const disabled = key === 'team' && shareableTeams.length === 0;
                  return (
                    <button
                      key={key}
                      type="button"
                      disabled={disabled}
                      onClick={() => setBoardModal((m) => ({ ...m, visibility: key }))}
                      className="modern-button-secondary"
                      style={{
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 6,
                        padding: '0.5rem',
                        borderRadius: '0.5rem',
                        fontSize: '0.8125rem',
                        // Follow the accent the user picked in Settings rather
                        // than a hardcoded blue — the app ships five presets.
                        border: active
                          ? '1.5px solid var(--accent)'
                          : '1.5px solid transparent',
                        background: active ? 'rgba(var(--accent-rgb), 0.10)' : undefined,
                        color: active ? 'var(--accent)' : undefined,
                        opacity: disabled ? 0.5 : 1,
                        cursor: disabled ? 'not-allowed' : 'pointer',
                      }}
                      title={disabled ? 'You are not an editor/admin/owner on any team' : undefined}
                    >
                      <Icon size={13} /> {label}
                    </button>
                  );
                })}
              </div>
              {boardModal.visibility === 'team' && shareableTeams.length > 0 && (
                <div className="modal-picker" style={{ display: 'flex', width: '100%', marginTop: '0.5rem' }}>
                  <CompactSelectPicker
                    value={boardModal.teamId}
                    onChange={(v) => setBoardModal((m) => ({ ...m, teamId: v }))}
                    options={shareableTeams.map((t) => ({ value: String(t.id), label: t.name }))}
                    aria-label="Team"
                  />
                </div>
              )}
              {boardModal.visibility === 'team' && (
                <p className="task-board-setting-hint" style={{ marginTop: '0.375rem' }}>
                  Owned by the team and realtime for all members — like a team workspace.
                </p>
              )}
            </div>
          )}
          <div className="board-edit-field">
            <label className="board-edit-label" htmlFor="board-edit-name">
              Name
            </label>
            <input
              id="board-edit-name"
              type="text"
              autoFocus
              className="modal-input board-edit-input"
              placeholder="e.g. Website Relaunch"
              value={boardModal.name}
              onChange={(e) => setBoardModal((m) => ({ ...m, name: e.target.value }))}
            />
          </div>
          <BoardKeyPrefixField boardModal={boardModal} setBoardModal={setBoardModal} takenPrefixes={takenPrefixes} />
          <div className="board-edit-field">
            <div className="board-edit-field-head">
              <label className="board-edit-label" htmlFor="board-edit-desc">
                Description
              </label>
              <span
                className={`board-edit-counter${descLength > BOARD_DESCRIPTION_MAX ? ' board-edit-counter--over' : ''}`}
                aria-label={`${descLength} of ${BOARD_DESCRIPTION_MAX} characters`}
              >
                {descLength} / {BOARD_DESCRIPTION_MAX}
              </span>
            </div>
            {/* The wrapper carries border, scroll bar and the resize grip (a
                textarea's own bar is native and cannot take the app's style). */}
            <OsScroll
              className="modal-input board-edit-input board-edit-desc modal-textarea-wrap"
              options={{ overflow: { x: 'hidden' } }}
            >
              <textarea
                id="board-edit-desc"
                ref={boardDescRef}
                className="modal-textarea-auto"
                rows={4}
                maxLength={BOARD_DESCRIPTION_MAX}
                placeholder="Optional short description…"
                value={boardModal.description}
                onChange={(e) => setBoardModal((m) => ({ ...m, description: e.target.value }))}
              />
            </OsScroll>
          </div>
          <BoardColorField color={boardModal.color} onChange={(color) => setBoardModal((m) => ({ ...m, color }))} />
          <BoardLabelEditor
            labels={boardModal.labels === null ? null : boardModal.labels || []}
            usage={boardModal.stats?.labelUsage}
            newId={newId}
            onChange={(labels) => setBoardModal((m) => ({ ...m, labels }))}
          />
          <BoardEditOptions
            showDoneTasks={boardModal.showDoneTasks !== false}
            onShowDoneTasks={(on) => setBoardModal((m) => ({ ...m, showDoneTasks: on, showDoneTouched: true }))}
            doneCount={boardModal.stats ? boardModal.stats.done : undefined}
            showColumnProgress={boardModal.showColumnProgress !== false}
            onShowColumnProgress={(on) => setBoardModal((m) => ({ ...m, showColumnProgress: on, showColumnProgressTouched: true }))}
            closeFromCommits={boardModal.closeFromCommits === true}
            onCloseFromCommits={(on) => setBoardModal((m) => ({ ...m, closeFromCommits: on, closeFromCommitsTouched: true }))}
          />
        </div>
      </OsScroll>
      <BoardEditFooter
        mode={boardModal.mode}
        canSubmit={!!boardModal.name.trim() && !keyPrefixError(boardModal, takenPrefixes)}
        onCancel={() => setBoardModal(null)}
        onSubmit={submitBoardModal}
        onDelete={boardModal.mode === 'edit' && onDeleteBoard ? onDeleteBoard : null}
      />
    </Modal>
  );
}
